import React from 'react';
import { Form, Input, Button, Card, Typography, Space, Select, Alert } from 'antd';
import { UserOutlined, LockOutlined, GlobalOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../../firebase';
import { useNavigate } from 'react-router-dom';

const { Title } = Typography;
const { Option } = Select;

const LANG_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
  { value: 'pt', label: 'Português' },
];

const Login: React.FC = () => {
  const { t, i18n } = useTranslation('auth');
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  const [alert, setAlert] = React.useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const onFinish = async (values: { email: string; password: string }) => {
    setLoading(true);
    setAlert(null);
    try {
      await signInWithEmailAndPassword(auth, values.email, values.password);
      setAlert({ type: 'success', message: t('login.success') });
      setTimeout(() => navigate('/dashboard'), 1000);
    } catch (error: any) {
      console.error('Login error:', error); // Debug log
      let errorMsg = t('login.genericError');
      if (error && (error.code || error.message)) {
        switch (error.code) {
          case 'auth/user-not-found':
            errorMsg = t('login.userNotFound');
            break;
          case 'auth/wrong-password':
            errorMsg = t('login.wrongPassword');
            break;
          case 'auth/too-many-requests':
            errorMsg = t('login.tooManyRequests');
            break;
          case 'auth/invalid-credential':
            errorMsg = t('login.wrongPassword');
            break;
          default:
            if (error.message && error.message.includes('INVALID_PASSWORD')) {
              errorMsg = t('login.wrongPassword');
            } else if (error.message && error.message.includes('EMAIL_NOT_FOUND')) {
              errorMsg = t('login.userNotFound');
            } else {
              errorMsg = t('login.error');
            }
        }
      }
      setAlert({ type: 'error', message: errorMsg || t('login.error') });
    } finally {
      setLoading(false);
    }
  };

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
  };

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '100vh',
      background: '#f0f2f5'
    }}>
      <Card 
        style={{ 
          width: '100%', 
          maxWidth: 400,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          borderRadius: 8
        }}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div style={{ textAlign: 'center' }}>
            <img 
              src="/logo-new.png" 
              alt="Logo" 
              style={{ height: 64, marginBottom: 16 }}
            />
            <Title level={2}>{t('login.title')}</Title>
          </div>

          {alert && (
            <Alert
              type={alert.type}
              message={alert.message}
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          <Form
            form={form}
            name="login"
            onFinish={onFinish}
            layout="vertical"
            requiredMark={false}
            size="large"
          >
            <Form.Item
              name="email"
              rules={[
                { required: true, message: t('login.emailRequired') },
                { type: 'email', message: t('login.invalidEmail') }
              ]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder={t('login.emailPlaceholder')}
                autoComplete="email"
              />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[{ required: true, message: t('login.passwordRequired') }]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder={t('login.passwordPlaceholder')}
                autoComplete="current-password"
              />
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit" block loading={loading}>
                {t('login.submit')}
              </Button>
            </Form.Item>
          </Form>

          <div style={{ textAlign: 'center' }}>
            <GlobalOutlined style={{ marginRight: 8 }} />
            <Select
              value={i18n.language}
              onChange={handleLanguageChange}
              style={{ width: 140 }}
              size="middle"
            >
              {LANG_OPTIONS.map(opt => (
                <Option key={opt.value} value={opt.value}>{opt.label}</Option>
              ))}
            </Select>
          </div>
        </Space>
      </Card>
    </div>
  );
};

export default Login; 